import { Injectable, inject } from "@angular/core";
import { Observable, BehaviorSubject, of, forkJoin, from } from "rxjs";
import { map, switchMap, catchError } from "rxjs/operators";
import { GridItem } from "@app/common/components/grid-layout/grid-layout.component";
import dayjs from "dayjs/esm";
import {
  ApiSchemas,
  BackendApiService,
  apiErrorMessage,
} from "@app/common/services/backend-api.service";

// Saved dashboard views and user-drawn chart series, served by the Fastify
// backend (/api/v1/portfolios/:id/dashboard-views, /api/v1/dashboard-views/:id,
// /api/v1/portfolios/:id/chart-series, /api/v1/chart-series/:id). The caller
// is identified by the Firebase token; `user_id` params are kept on the
// method signatures for their callers but never sent.

// The three jsonb columns hold whatever the dashboard stores.
export type DashboardView = Omit<
  ApiSchemas["DashboardView"],
  "gridLayout" | "gridFilters" | "dateRange"
> & { gridLayout: any; gridFilters: any; dateRange: any };
export type ChartSeries = ApiSchemas["ChartSeries"];
export type ChartDataPoint = ApiSchemas["ChartDataPoint"];

export interface DateRange {
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}

// Define a more specific type for the date range expected by createView
interface CreateViewDateRange {
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  preset?: string | null; // Add preset
}

// Custom chart series interfaces
export interface CustomDataPoint {
  date: string;
  value: number;
}

export interface CustomSeriesCreate {
  user_id: string;
  portfolio_id: number;
  series_name: string;
  dashboard_view_id?: number | null;
  data_points: { series_id?: number; date: string; value: number }[];
}

export interface CustomSeriesUpdate {
  series_id: number;
  series_name: string;
  data_points: { series_id?: number; date: string; value: number }[];
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

// The chart dialog stores dates as "M-D-YY"; the API wants YYYY-MM-DD (the
// database column is a DATE, which is also how points read back).
export function toIsoDay(date: string): string {
  if (ISO_DAY.test(date)) return date;
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? date : dayjs(parsed).format("YYYY-MM-DD");
}

function toApiPoints(points: { date: string; value: number }[] | undefined) {
  return (points || []).map((point) => ({
    date: toIsoDay(point.date),
    value: Number(point.value),
  }));
}

@Injectable({
  providedIn: "root",
})
export class DashboardDataService {
  private api = inject(BackendApiService);

  // Bumped by forceRefresh(); live getUserDashboardViews subscriptions reload.
  private refresh$ = new BehaviorSubject<number>(0);

  getUserDashboardViews(params: {
    user_id: string;
    portfolio_id: number;
  }): Observable<DashboardView[]> {
    return this.refresh$.pipe(
      switchMap(() =>
        from(
          this.api.client.GET("/api/v1/portfolios/{id}/dashboard-views", {
            params: { path: { id: params.portfolio_id } },
          })
        )
      ),
      map(({ data, error }) => {
        if (error || !data) {
          throw new Error(apiErrorMessage(error, "Dashboard views load failed."));
        }
        return data as DashboardView[];
      })
    );
  }

  /**
   * Get a dashboard view by ID (null when it does not exist or is not the caller's)
   */
  getDashboardViewById(viewId: number): Observable<DashboardView | null> {
    const numericViewId =
      typeof viewId === "number" ? viewId : parseInt(String(viewId), 10);
    if (isNaN(numericViewId)) {
      console.error("Invalid viewId provided to getDashboardViewById:", viewId);
      return of(null);
    }
    return from(
      this.api.client.GET("/api/v1/dashboard-views/{viewId}", {
        params: { path: { viewId: numericViewId } },
      })
    ).pipe(
      map(({ data, error, response }) => {
        if (response?.status === 404) return null;
        if (error || !data) {
          throw new Error(apiErrorMessage(error, "Dashboard view load failed."));
        }
        return data as DashboardView;
      })
    );
  }

  forceRefresh(): void {
    this.refresh$.next(Date.now());
  }

  updateDashboardViewLayout(params: {
    id: number;
    grid_layout: any[];
    grid_filters?: any;
  }): Observable<boolean> {
    return from(
      this.api.client.PATCH("/api/v1/dashboard-views/{viewId}", {
        params: { path: { viewId: params.id } },
        body: {
          gridLayout: params.grid_layout,
          gridFilters: params.grid_filters || {},
        },
      })
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          throw new Error(apiErrorMessage(error, "Dashboard view was not updated."));
        }
        return !!data;
      })
    );
  }

  createView(params: {
    title: string;
    description?: string;
    public: boolean;
    grid_layout: GridItem[];
    grid_filters: {
      selectedSymbols: string[];
      compareChartSymbols?: string[];
    };
    date_range: CreateViewDateRange;
    user_id: string;
    portfolio_id: number;
  }): Observable<DashboardView | null> {
    // Convert dates to ISO strings if they are Date objects and if they exist
    const dateRange: {
      startDate?: string;
      endDate?: string;
      preset?: string | null;
    } = {};

    if (params.date_range.startDate) {
      dateRange.startDate =
        params.date_range.startDate instanceof Date
          ? dayjs(params.date_range.startDate).format("YYYY-MM-DD")
          : params.date_range.startDate;
    }

    if (params.date_range.endDate) {
      dateRange.endDate =
        params.date_range.endDate instanceof Date
          ? dayjs(params.date_range.endDate).format("YYYY-MM-DD")
          : params.date_range.endDate;
    }

    // Copy the preset if it exists
    if (params.date_range.preset) {
      dateRange.preset = params.date_range.preset;
    }

    return from(
      this.api.client.POST("/api/v1/portfolios/{id}/dashboard-views", {
        params: { path: { id: params.portfolio_id } },
        body: {
          title: params.title,
          description: params.description || null,
          public: params.public,
          gridLayout: params.grid_layout,
          gridFilters: params.grid_filters,
          dateRange,
        },
      })
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          throw new Error(apiErrorMessage(error, "Dashboard view was not saved."));
        }
        this.forceRefresh();
        return data as DashboardView;
      })
    );
  }

  deleteView(id: number): Observable<boolean> {
    return from(
      this.api.client.DELETE("/api/v1/dashboard-views/{viewId}", {
        params: { path: { viewId: id } },
      })
    ).pipe(
      map(({ error, response }) => {
        if (response?.status === 404) return false;
        if (error) {
          throw new Error(apiErrorMessage(error, "Dashboard view was not deleted."));
        }
        this.forceRefresh();
        return true;
      })
    );
  }

  // ====================
  // CUSTOM CHART SERIES METHODS
  // ====================

  /**
   * Get the caller's custom chart series for a portfolio. `dashboard_view_id`
   * 0 (no view selected) returns the series not grouped under any view.
   */
  getCustomChartSeries(params: {
    user_id: string;
    portfolio_id: number;
    dashboard_view_id: number;
  }): Observable<ChartSeries[]> {
    return this.listCustomChartSeries(
      params.portfolio_id,
      params.dashboard_view_id ?? 0
    );
  }

  private listCustomChartSeries(
    portfolioId: number,
    dashboardViewId?: number
  ): Observable<ChartSeries[]> {
    return from(
      this.api.client.GET("/api/v1/portfolios/{id}/chart-series", {
        params: {
          path: { id: portfolioId },
          query: dashboardViewId == null ? {} : { dashboardViewId },
        },
      })
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          throw new Error(apiErrorMessage(error, "Custom chart series load failed."));
        }
        return data;
      })
    );
  }

  /**
   * Create a new custom chart series with data points (one request)
   */
  createCustomChartSeries(
    params: CustomSeriesCreate
  ): Observable<ChartSeries | null> {
    return from(
      this.api.client.POST("/api/v1/portfolios/{id}/chart-series", {
        params: { path: { id: params.portfolio_id } },
        body: {
          seriesName: params.series_name,
          dashboardViewId: params.dashboard_view_id || null,
          dataPoints: toApiPoints(params.data_points),
        },
      })
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          throw new Error(apiErrorMessage(error, "Failed to create chart series"));
        }
        return data;
      }),
      catchError((error) => {
        console.error("Error creating custom chart series:", error);
        throw error;
      })
    );
  }

  /**
   * Update an existing custom chart series and replace its data points
   */
  updateCustomChartSeries(params: CustomSeriesUpdate): Observable<boolean> {
    return from(
      this.api.client.PUT("/api/v1/chart-series/{seriesId}", {
        params: { path: { seriesId: params.series_id } },
        body: {
          seriesName: params.series_name,
          dataPoints: toApiPoints(params.data_points),
        },
      })
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          throw new Error(apiErrorMessage(error, "Failed to update chart series"));
        }
        return true;
      }),
      catchError((error) => {
        console.error("Error updating custom chart series:", error);
        throw error;
      })
    );
  }

  /**
   * Delete a custom chart series (its data points go with it)
   */
  deleteCustomChartSeries(seriesId: number): Observable<boolean> {
    return from(
      this.api.client.DELETE("/api/v1/chart-series/{seriesId}", {
        params: { path: { seriesId } },
      })
    ).pipe(
      map(({ error, response }) => {
        if (response?.status === 404) return false;
        if (error) {
          throw new Error(apiErrorMessage(error, "Failed to delete chart series"));
        }
        return true;
      })
    );
  }

  /**
   * Add data points to an existing custom chart series
   */
  addCustomChartDataPoints(params: {
    series_id: number;
    data_points: { date: string; value: number }[];
  }): Observable<boolean> {
    if (!params.data_points?.length) return of(false);
    return from(
      this.api.client.POST("/api/v1/chart-series/{seriesId}/points", {
        params: { path: { seriesId: params.series_id } },
        body: { dataPoints: toApiPoints(params.data_points) },
      })
    ).pipe(
      map(({ data, error }) => {
        if (error || !data) {
          throw new Error(apiErrorMessage(error, "Failed to add data points"));
        }
        return true;
      })
    );
  }

  /**
   * Delete all custom chart series for a specific portfolio
   * This is used when deleting a portfolio to avoid foreign key constraint violations
   */
  deleteAllCustomChartSeriesByPortfolio(params: {
    user_id: string;
    portfolio_id: number;
  }): Observable<boolean> {
    return this.listCustomChartSeries(params.portfolio_id).pipe(
      switchMap((series) => {
        if (series.length === 0) {
          // No series to delete, return success
          return of(true);
        }
        return forkJoin(series.map((s) => this.deleteCustomChartSeries(s.id))).pipe(
          map((results: boolean[]) => results.every((result) => result === true))
        );
      }),
      catchError((error) => {
        console.error(
          "Error deleting custom chart series for portfolio:",
          error
        );
        return of(false);
      })
    );
  }
}
